import React from "react";

type HeadingLevel = 1 | 2 | 3;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  level?: HeadingLevel;
};

/**
 * Display heading — the marketing scale (72/52/22px).
 * Use <Gradient> to color a phrase with the pink-to-purple gradient,
 * and <EmLine> to force a stacked line (block) within an h1.
 */
export function Heading({ level = 2, className, ...rest }: HeadingProps) {
  const Tag = `h${level}` as "h1" | "h2" | "h3";
  const cls = [`tb-h${level}`, className].filter(Boolean).join(" ");
  return <Tag className={cls} {...rest} />;
}

type SectionHeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  level?: HeadingLevel;
};

/**
 * Compact dashboard / product page heading at 30px. Use this instead of
 * <Heading level={1}> on dense pages where the marketing-scale H1 is too big.
 */
export function SectionHeading({
  level = 1,
  className,
  ...rest
}: SectionHeadingProps) {
  const Tag = `h${level}` as "h1" | "h2" | "h3";
  const cls = ["tb-h-section", className].filter(Boolean).join(" ");
  return <Tag className={cls} {...rest} />;
}

type RowHeadingProps = React.HTMLAttributes<HTMLHeadingElement> & {
  level?: HeadingLevel;
};

/**
 * Card / row title at 17px. Used inside list rows, stat tiles, and small cards.
 */
export function RowHeading({ level = 3, className, ...rest }: RowHeadingProps) {
  const Tag = `h${level}` as "h1" | "h2" | "h3";
  const cls = ["tb-h-row", className].filter(Boolean).join(" ");
  return <Tag className={cls} {...rest} />;
}

export function Gradient({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const cls = ["tb-grad", className].filter(Boolean).join(" ");
  return <span className={cls}>{children}</span>;
}

export function EmLine({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const cls = ["tb-em-line", className].filter(Boolean).join(" ");
  return <span className={cls}>{children}</span>;
}
